import { useState, useEffect, useRef } from 'react'
import ThemeToggle from './ThemeToggle'
import HeaderShortcuts from './HeaderShortcuts'
import NewMenu from './NewMenu'
import { draftsAwaitingSend } from '../lib/draftInvoices'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import TimerWidget from './TimerWidget'
import { pageTitle } from '../lib/pageTitles'
import { clientPath, projectPath, invoicePath } from '../lib/recordLinks'

// The client's and contractor's own names for their pages; an admin's come
// from pageTitles, which names every page the way the sidebar does.
//
// These used to carry a call-to-action each. A button here sends its page
// `?new=1`, and none of the client side's acted on it (four went nowhere), so
// they are gone rather than left to look broken. Admins start things from the
// + New menu.
const CLIENT_PAGE_TITLES = {
  '/dashboard':  'Overview',
  '/onboarding': 'Onboarding',
  '/projects':   'My Project',
  '/files':      'Files & Assets',
  '/invoices':   'Invoices',
  '/approvals':  'Approvals',
  '/proposals':  'Proposals',
  '/brand-assets': 'Brand Kit',
  '/vault': 'Brand Vault',
  '/forms':      'Forms',
}

export default function TopBar({ onMenuClick, sidebarOpen, onCmdK }) {
  const location = useLocation()
  const { client, isAdmin } = useAuth()

  const navigate = useNavigate()
  // Named as the sidebar names it, so the header, the tab and the menu agree.
  const page = {
    title: isAdmin
      ? (pageTitle(location.pathname) || 'Portal')
      : (CLIENT_PAGE_TITLES[location.pathname] || pageTitle(location.pathname, { admin: false }) || 'Portal'),
  }
  // The admin has no client record, so the avatar read "??" and "Client".
  const whoName = isAdmin ? 'Abstract Creative' : client?.name
  const initials = whoName ? whoName.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase() : '??'

  // Sound notifications (admin)
  const [toasts, setToasts] = useState([])

  function playChime(type) {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const notes = type === 'payment' ? [523, 659, 784] : [659, 784]
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.frequency.value = freq
        osc.type = 'sine'
        const t = ctx.currentTime + i * 0.18
        gain.gain.setValueAtTime(0, t)
        gain.gain.linearRampToValueAtTime(0.25, t + 0.04)
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35)
        osc.start(t)
        osc.stop(t + 0.35)
      })
    } catch {}
  }

  function addToast(type, title, body) {
    const id = Date.now()
    setToasts(prev => [...prev, { id, type, title, body }])
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 5000)
  }

  useEffect(() => {
    if (!isAdmin) return
    const msgSub = supabase
      .channel('admin-msg-notif')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, payload => {
        if (payload.new.sender !== 'client') return
        playChime('message')
        addToast('message', 'New Message', payload.new.body?.slice(0, 80) || 'A client sent you a message')
      })
      .subscribe()

    const invSub = supabase
      .channel('admin-inv-notif')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'invoices' }, payload => {
        if (payload.new.status !== 'paid' || payload.old?.status === 'paid') return
        playChime('payment')
        const amt = payload.new.amount ? `$${Number(payload.new.amount).toFixed(2)}` : ''
        addToast('payment', 'Invoice Paid', `${payload.new.invoice_number || 'An invoice'}${amt ? ` for ${amt}` : ''} was just paid`)
      })
      .subscribe()

    return () => {
      supabase.removeChannel(msgSub)
      supabase.removeChannel(invSub)
    }
  }, [isAdmin])

  useEffect(() => {
    document.title = page.title ? `${page.title} — Abstract Creative` : 'Abstract Creative'
  }, [page.title])

  // Admin notification center
  const [adminNotifOpen, setAdminNotifOpen] = useState(false)
  const [adminUnreadMsgs, setAdminUnreadMsgs] = useState([])
  const [adminPendingApprovals, setAdminPendingApprovals] = useState([])
  const [adminDraftInvoices, setAdminDraftInvoices] = useState([])
  // Forms a client has submitted that nobody has opened yet.
  const [adminNewForms, setAdminNewForms] = useState([])
  const adminNotifRef = useRef(null)
  const adminNotifTotal = adminUnreadMsgs.length + adminPendingApprovals.length + adminNewForms.length

  useEffect(() => {
    if (!isAdmin) return
    async function loadAdminNotifs() {
      const [{ data: msgs }, { data: aprs }, { data: drafts }, { data: forms }] = await Promise.all([
        supabase.from('messages').select('id, client_id, body, created_at, clients(name)').eq('sender', 'client').eq('read', false).order('created_at', { ascending: false }).limit(10),
        supabase.from('approvals').select('id, client_id, status, created_at, clients(name), deliverables(title)').eq('status', 'pending').order('created_at', { ascending: false }).limit(10),
        // Invoices written and never sent — money nobody has been asked for.
        supabase.from('invoices').select('id, amount, status, issue_date, created_at').eq('status', 'draft'),
        supabase.from('client_forms').select('id, title, project_id, submitted_at, clients(name)')
          .eq('status', 'submitted').is('reviewed_at', null).order('submitted_at', { ascending: false }).limit(10),
      ])
      setAdminUnreadMsgs(msgs || [])
      setAdminPendingApprovals(aprs || [])
      setAdminDraftInvoices(draftsAwaitingSend(drafts || []))
      setAdminNewForms(forms || [])
    }
    loadAdminNotifs()
    const interval = setInterval(loadAdminNotifs, 60000)
    return () => clearInterval(interval)
  }, [isAdmin])

  useEffect(() => {
    if (!adminNotifOpen) return
    function onClickOutside(e) {
      if (adminNotifRef.current && !adminNotifRef.current.contains(e.target)) setAdminNotifOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [adminNotifOpen])

  // Client notifications
  // Search
  const [searchQ, setSearchQ] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchOpen, setSearchOpen] = useState(false)
  const searchRef = useRef(null)

  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults([]); setSearchOpen(false); return }
    const searchDebounce = setTimeout(async () => {
      const q = searchQ.trim().toLowerCase()
      try {
        if (isAdmin) {
          const [{ data: cls }, { data: projs }, { data: invs }, { data: fls }] = await Promise.all([
            supabase.from('clients').select('id, name, company').or(`name.ilike.%${q}%,company.ilike.%${q}%`).limit(4),
            supabase.from('projects').select('id, title, clients(name)').ilike('title', `%${q}%`).eq('archived', false).limit(5),
            supabase.from('invoices').select('id, invoice_number, amount, status').ilike('invoice_number', `%${q}%`).limit(3),
            supabase.from('files').select('id, name, clients(name)').ilike('name', `%${q}%`).limit(3),
          ])
          const results = [
            ...(cls || []).map(c => ({ type: 'client',  id: c.id, label: c.name,            sub: c.company,            path: clientPath(c.id) })),
            ...(projs || []).map(p => ({ type: 'project', id: p.id, label: p.title,          sub: p.clients?.name,      path: projectPath(p.id) })),
            ...(invs || []).map(i => ({ type: 'invoice', id: i.id, label: i.invoice_number, sub: `$${Number(i.amount||0).toLocaleString()} · ${i.status}`, path: invoicePath(i.id) })),
            ...(fls || []).map(f => ({ type: 'file',    id: f.id, label: f.name,            sub: f.clients?.name,      path: '/files' })),
          ]
          setSearchResults(results)
          setSearchOpen(results.length > 0)
        } else if (client?.id) {
          const [{ data: files }, { data: invs }] = await Promise.all([
            supabase.from('files').select('id, name').eq('client_id', client.id).ilike('name', `%${q}%`).limit(4),
            supabase.from('invoices').select('id, invoice_number, description').eq('client_id', client.id).ilike('description', `%${q}%`).limit(3),
          ])
          const results = [
            ...(files || []).map(f => ({ type: 'file', id: f.id, label: f.name, sub: 'File', path: '/files' })),
            ...(invs || []).map(i => ({ type: 'invoice', id: i.id, label: i.invoice_number, sub: i.description, path: '/invoices' })),
          ]
          setSearchResults(results)
          setSearchOpen(results.length > 0)
        }
      } catch (err) {
        console.error('Search error:', err)
      }
    }, 250)
    return () => clearTimeout(searchDebounce)
  }, [searchQ, isAdmin, client?.id])

  useEffect(() => {
    if (!searchOpen) return
    function handler(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false); setSearchQ('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [searchOpen])

  // Password change
  const [pwOpen, setPwOpen] = useState(false)
  const [pwCurrent, setPwCurrent] = useState('')
  const [pwNew, setPwNew] = useState('')
  const [pwConfirm, setPwConfirm] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwError, setPwError] = useState('')
  const [pwDone, setPwDone] = useState(false)

  async function changePassword() {
    if (!pwNew || pwNew !== pwConfirm) { setPwError('Passwords do not match'); return }
    if (pwNew.length < 8) { setPwError('Password must be at least 8 characters'); return }
    setPwSaving(true); setPwError('')
    const { error } = await supabase.auth.updateUser({ password: pwNew })
    setPwSaving(false)
    if (error) { setPwError(error.message); return }
    setPwDone(true)
    setTimeout(() => { setPwOpen(false); setPwNew(''); setPwConfirm(''); setPwDone(false) }, 2000)
  }

  const [notifOpen, setNotifOpen] = useState(false)
  const [notifFiles, setNotifFiles] = useState([])
  const [notifApprovals, setNotifApprovals] = useState([])
  const [notifBriefs, setNotifBriefs] = useState([])
  const [notifMessages, setNotifMessages] = useState([])
  const [notifInvoicesDue, setNotifInvoicesDue] = useState([])
  const notifRef = useRef(null)
  const notifSeenKey = client?.id ? `ac_notif_seen_${client.id}` : null
  const notifDismissedKey = client?.id ? `ac_notif_dismissed_${client.id}` : null
  const [dismissed, setDismissed] = useState(() => notifDismissedKey ? (localStorage.getItem(notifDismissedKey) || '1970-01-01') : '1970-01-01')

  useEffect(() => {
    if (isAdmin || !client?.id) return
    async function fetchNotifs() {
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
      const today = new Date().toISOString().split('T')[0]
      const soon = new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0]
      const [{ data: files }, { data: approvals }, { data: briefs }, { data: messages }, { data: invoicesDue }] = await Promise.all([
        supabase.from('files').select('id, name, uploaded_at').eq('client_id', client.id).gte('uploaded_at', since).order('uploaded_at', { ascending: false }),
        supabase.from('approvals').select('id, title, created_at').eq('client_id', client.id).eq('status', 'pending').order('created_at', { ascending: false }),
        supabase.from('project_briefs').select('id, created_at, projects(title)').eq('client_id', client.id).eq('status', 'pending').order('created_at', { ascending: false }),
        supabase.from('messages').select('id, body, created_at').eq('client_id', client.id).eq('sender', 'admin').gte('created_at', since).order('created_at', { ascending: false }),
        supabase.from('invoices').select('id, invoice_number, amount, due_date, status').eq('client_id', client.id).in('status', ['sent', 'overdue']).gte('due_date', today).lte('due_date', soon).order('due_date'),
      ])
      setNotifFiles(files || [])
      setNotifApprovals(approvals || [])
      setNotifBriefs(briefs || [])
      setNotifMessages(messages || [])
      setNotifInvoicesDue(invoicesDue || [])
    }
    fetchNotifs()
  }, [client?.id, isAdmin])

  useEffect(() => {
    if (!notifOpen) return
    function handleClick(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [notifOpen])

  const lastSeen = notifSeenKey ? (localStorage.getItem(notifSeenKey) || '1970-01-01') : '1970-01-01'
  const newFiles = notifFiles.filter(f => (f.uploaded_at || '') > lastSeen).length
  const newApprovals = notifApprovals.filter(a => a.created_at > lastSeen).length
  const newBriefs = notifBriefs.filter(b => b.created_at > lastSeen).length
  const newMessages = notifMessages.filter(m => m.created_at > lastSeen).length
  const unreadCount = newFiles + newApprovals + newBriefs + newMessages

  function openNotifs() {
    setNotifOpen(o => !o)
    if (!notifOpen && notifSeenKey) localStorage.setItem(notifSeenKey, new Date().toISOString())
  }

  function markAllRead() {
    const now = new Date().toISOString()
    if (notifSeenKey) localStorage.setItem(notifSeenKey, now)
    if (notifDismissedKey) {
      localStorage.setItem(notifDismissedKey, now)
      setDismissed(now)
    }
  }

  return (
    <>
    {toasts.length > 0 && (
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 3000, display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none' }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            background: 'var(--card)', border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: 12, padding: '12px 16px', minWidth: 280, maxWidth: 340,
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)', pointerEvents: 'all',
            borderLeft: `4px solid ${t.type === 'payment' ? '#059669' : '#2563EB'}`,
          }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: t.type === 'payment' ? '#D1FAE5' : '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {t.type === 'payment' ? (
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1" y="3" width="13" height="9" rx="2" stroke="#059669" strokeWidth="1.3"/><path d="M4 7.5h3M10 7.5h1" stroke="#059669" strokeWidth="1.3" strokeLinecap="round"/></svg>
              ) : (
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M2 11V4l5.5 4L13 4v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" stroke="#2563EB" strokeWidth="1.2" strokeLinejoin="round"/><path d="M2 4l5.5 4L13 4" stroke="#2563EB" strokeWidth="1.2" strokeLinejoin="round"/></svg>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'var(--fd)', fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 2 }}>{t.title}</div>
              <div style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--mut)', lineHeight: 1.4 }}>{t.body}</div>
            </div>
            <button onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--hint)', flexShrink: 0, lineHeight: 1, marginTop: 1 }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
            </button>
          </div>
        ))}
      </div>
    )}
    {pwOpen && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: 'var(--card)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 400 }}>
          <div style={{ fontFamily: 'var(--fd)', fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginBottom: 20 }}>Change Password</div>
          {pwDone ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>✓</div>
              <div style={{ fontFamily: 'var(--fu)', fontSize: 14, color: '#059669', fontWeight: 600 }}>Password updated!</div>
            </div>
          ) : (
            <>
              {[
                { label: 'New Password', val: pwNew, set: setPwNew },
                { label: 'Confirm New Password', val: pwConfirm, set: setPwConfirm },
              ].map(f => (
                <div key={f.label} style={{ marginBottom: 14 }}>
                  <label style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--mut)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{f.label}</label>
                  <input type="password" value={f.val} onChange={e => f.set(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, fontFamily: 'var(--fu)', fontSize: 13, outline: 'none' }} />
                </div>
              ))}
              {pwError && <div style={{ fontFamily: 'var(--fu)', fontSize: 12, color: 'var(--red)', marginBottom: 14 }}>{pwError}</div>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
                <button onClick={() => { setPwOpen(false); setPwNew(''); setPwConfirm(''); setPwError('') }}
                  style={{ padding: '8px 16px', background: 'none', border: '1px solid var(--bdr)', borderRadius: 7, fontFamily: 'var(--fu)', fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={changePassword} disabled={pwSaving}
                  style={{ padding: '8px 20px', background: 'var(--accent-soft)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', fontFamily: 'var(--fu)', fontSize: 13, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer' }}>
                  {pwSaving ? 'Saving…' : 'Update Password'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )}
    <div style={{
      height: 56, background: 'var(--card)', borderBottom: '1px solid var(--bdr)',
      display: 'flex', alignItems: 'center', padding: '0 16px 0 24px', gap: 6,
      position: 'sticky', top: 0, zIndex: 50,
    }}>
      {/* Hamburger — mobile only */}
      <button
        onClick={onMenuClick}
        className="topbar-hamburger tb-icon"
        style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', color: 'var(--navy)', flexShrink: 0 }}
        aria-label="Toggle menu"
      >
        {sidebarOpen ? (
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M2.5 5h13M2.5 9h13M2.5 13h13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        )}
      </button>

      <div style={{
        fontFamily: "var(--fd)", fontSize: 16, fontWeight: 700,
        color: 'var(--navy)', flex: 1, letterSpacing: '-0.005em',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {page.title}
      </div>

      {/* Search — hidden on mobile */}
      <div ref={searchRef} className="topbar-search" style={{ position: 'relative', flexShrink: 0 }}>
        {/* On brand and square: teal outline while in use, and the glass
            lifts so it is obvious where typing goes (index.css, .tb-search). */}
        <div className="tb-search" style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '0 10px 0 12px', height: 36, boxSizing: 'border-box', width: 300, marginRight: 6,
        }}>
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <circle cx="5" cy="5" r="4" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M8.5 8.5l2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <input
            placeholder="Search clients, projects, files"
            aria-label="Search clients, projects, files"
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
            style={{
              border: 'none', background: 'transparent', outline: 'none',
              fontFamily: "var(--fu)", fontSize: 13, color: 'var(--txt)', width: '100%',
            }}
          />
          {onCmdK && (
            <button onClick={onCmdK} title="Open command palette (⌘K)" aria-label="Open command palette" className="tb-kbd"
              style={{ flexShrink: 0, background: 'var(--card)', border: '1px solid var(--bdr2)', padding: '2px 6px', fontFamily: 'var(--fu)', fontSize: 10, fontWeight: 700, color: 'var(--mut)', cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1.4 }}>
              ⌘K
            </button>
          )}
        </div>
        {searchOpen && searchResults.length > 0 && (
          <div style={{ position: 'absolute', top: 36, left: 0, width: 260, background: 'var(--card)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 200, overflow: 'hidden' }}>
            {searchResults.map((r, i) => (
              <button key={r.id + r.type} onClick={() => { navigate(r.path); setSearchOpen(false); setSearchQ('') }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderBottom: i < searchResults.length - 1 ? '1px solid var(--bdr)' : 'none' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--off)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                <div style={{ width: 24, height: 24, borderRadius: 6, background: r.type === 'client' ? 'rgba(24,60,68,0.09)' : r.type === 'project' ? 'rgba(239,66,55,0.10)' : r.type === 'invoice' ? 'rgba(229,216,206,0.6)' : 'rgba(94,138,145,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {r.type === 'client' && <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><circle cx="5.5" cy="3.5" r="2" stroke="#183C44" strokeWidth="1.2"/><path d="M1.5 9.5c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke="#183C44" strokeWidth="1.2" strokeLinecap="round"/></svg>}
                  {r.type === 'project' && <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><rect x="1.5" y="2" width="8" height="7" rx="1.5" stroke="#EF4237" strokeWidth="1.2"/><path d="M3.5 5h4M3.5 7h2" stroke="#EF4237" strokeWidth="1.1" strokeLinecap="round"/></svg>}
                  {r.type === 'file' && <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M2 2h5l2 2v6H2V2z" stroke="#5E8A91" strokeWidth="1.2" strokeLinejoin="round"/></svg>}
                  {r.type === 'invoice' && <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><rect x="1.5" y="1" width="8" height="9" rx="1.5" stroke="#8C7462" strokeWidth="1.2"/><path d="M3.5 4h4M3.5 6h3M3.5 8h2" stroke="#8C7462" strokeWidth="1.1" strokeLinecap="round"/></svg>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</div>
                  {r.sub && <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.sub}</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Running timers, as many as are going. Admin only. */}
      {isAdmin && <TimerWidget />}

      {/* Message shortcut icon */}
      {isAdmin ? (
        <button onClick={() => navigate('/messages')} title="Messages" aria-label="Messages" className="tb-icon" aria-current={location.pathname === '/messages' ? 'page' : undefined}
          style={{ position: 'relative', width: 36, height: 36, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: location.pathname === '/messages' ? 'var(--off)' : 'none', border: location.pathname === '/messages' ? '1px solid var(--bdr)' : '1px solid transparent', borderRadius: 7, cursor: 'pointer', padding: 0 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 3h12a1 1 0 011 1v7a1 1 0 01-1 1H5L2 14.5V4a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
          </svg>
          {adminUnreadMsgs.length > 0 && (
            <span style={{ position: 'absolute', top: 3, right: 3, minWidth: 14, height: 14, borderRadius: 7, background: 'var(--red)', border: '1.5px solid var(--card)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--fu)', fontSize: 8, fontWeight: 700, color: '#fff', lineHeight: 1, padding: '0 3px' }}>
              {adminUnreadMsgs.length > 9 ? '9+' : adminUnreadMsgs.length}
            </span>
          )}
        </button>
      ) : (
        <button onClick={() => navigate('/messages')} title="Messages" aria-label="Messages" className="tb-icon" aria-current={location.pathname === '/messages' ? 'page' : undefined}
          style={{ position: 'relative', width: 36, height: 36, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: location.pathname === '/messages' ? 'var(--off)' : 'none', border: location.pathname === '/messages' ? '1px solid var(--bdr)' : '1px solid transparent', borderRadius: 7, cursor: 'pointer', padding: 0 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 3h12a1 1 0 011 1v7a1 1 0 01-1 1H5L2 14.5V4a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
          </svg>
          {newMessages > 0 && (
            <span style={{ position: 'absolute', top: 3, right: 3, minWidth: 14, height: 14, borderRadius: 7, background: 'var(--red)', border: '1.5px solid var(--card)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--fu)', fontSize: 8, fontWeight: 700, color: '#fff', lineHeight: 1, padding: '0 3px' }}>
              {newMessages > 9 ? '9+' : newMessages}
            </span>
          )}
        </button>
      )}

      {/* Admin notification center */}
      {isAdmin && (
        <div ref={adminNotifRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button onClick={() => setAdminNotifOpen(o => !o)} className="tb-icon" aria-expanded={adminNotifOpen}
            style={{ position: 'relative', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: adminNotifOpen ? 'var(--off)' : 'none', border: adminNotifOpen ? '1px solid var(--bdr)' : '1px solid transparent', borderRadius: 7, cursor: 'pointer', padding: 0 }}
            aria-label="Admin notifications">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 2a4.5 4.5 0 0 0-4.5 4.5c0 2.5-.8 3.5-1.5 4h12c-.7-.5-1.5-1.5-1.5-4A4.5 4.5 0 0 0 8 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
              <path d="M6.5 13a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            {adminNotifTotal > 0 && (
              <span style={{ position: 'absolute', top: 3, right: 3, minWidth: 14, height: 14, borderRadius: 7, background: 'var(--red)', border: '1.5px solid var(--card)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--fu)', fontSize: 8, fontWeight: 700, color: '#fff', lineHeight: 1, padding: '0 3px' }}>
                {adminNotifTotal > 9 ? '9+' : adminNotifTotal}
              </span>
            )}
          </button>

          {adminNotifOpen && (
            <div style={{ position: 'absolute', top: 40, right: 0, width: 320, background: 'var(--card)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 200, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--bdr)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'var(--fd)', fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Inbox</span>
                {adminNotifTotal === 0 && <span style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)' }}>All clear</span>}
              </div>
              <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                {adminNotifTotal === 0 ? (
                  <div style={{ padding: '28px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 22, marginBottom: 8 }}>✅</div>
                    <div style={{ fontFamily: 'var(--fu)', fontSize: 12, color: 'var(--hint)' }}>No pending items</div>
                  </div>
                ) : (
                  <>
                    {adminUnreadMsgs.length > 0 && (
                      <div>
                        <div style={{ padding: '8px 16px 4px', fontFamily: 'var(--fu)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--hint)' }}>Unread Messages ({adminUnreadMsgs.length})</div>
                        {adminUnreadMsgs.map(m => (
                          <button key={m.id} onClick={() => { navigate('/messages'); setAdminNotifOpen(false) }}
                            style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--off)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                            <div style={{ width: 28, height: 28, borderRadius: 7, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                              <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1.5 2h10a1 1 0 011 1v6a1 1 0 01-1 1H4l-3 2.5V3a1 1 0 011-1z" stroke="#2563EB" strokeWidth="1.2" strokeLinejoin="round"/></svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 1 }}>{m.clients?.name || 'Client'}</div>
                              <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.body?.slice(0, 60) || '…'}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {adminNewForms.length > 0 && (
                      <div>
                        <div style={{ padding: '8px 16px 4px', fontFamily: 'var(--fu)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--hint)' }}>Forms Submitted ({adminNewForms.length})</div>
                        {adminNewForms.map(f => (
                          <button key={f.id} onClick={() => { navigate(projectPath(f.project_id, 'Forms')); setAdminNotifOpen(false) }}
                            style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--off)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                            <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ color: 'var(--success)' }}><rect x="2" y="1.5" width="9" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M4.5 6.5l1.5 1.5 2.5-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 1 }}>{f.title}</div>
                              <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)' }}>{f.clients?.name || 'Client'} · submitted</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {adminPendingApprovals.length > 0 && (
                      <div>
                        <div style={{ padding: '8px 16px 4px', fontFamily: 'var(--fu)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--hint)' }}>Pending Approvals ({adminPendingApprovals.length})</div>
                        {adminPendingApprovals.map(a => (
                          <button key={a.id} onClick={() => { navigate('/approvals'); setAdminNotifOpen(false) }}
                            style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--off)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                            <div style={{ width: 28, height: 28, borderRadius: 7, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                              <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 1.5v5.5l3 2" stroke="#D97706" strokeWidth="1.3" strokeLinecap="round"/><circle cx="6.5" cy="6.5" r="5.5" stroke="#D97706" strokeWidth="1.3"/></svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 1 }}>{a.deliverables?.title || 'Approval needed'}</div>
                              <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)' }}>{a.clients?.name || '—'} · pending review</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div style={{ padding: '10px 16px', borderTop: '1px solid var(--bdr)', display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button onClick={() => { navigate('/messages'); setAdminNotifOpen(false) }} style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--hint)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Messages →</button>
                <button onClick={() => { navigate('/approvals'); setAdminNotifOpen(false) }} style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--hint)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Approvals →</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notification bell — client only */}
      {!isAdmin && (
        <div ref={notifRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={openNotifs} className="tb-icon" aria-expanded={notifOpen}
            style={{ position: 'relative', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: notifOpen ? 'var(--off)' : 'none', border: notifOpen ? '1px solid var(--bdr)' : '1px solid transparent', borderRadius: 7, cursor: 'pointer', padding: 0 }}
            aria-label="Notifications"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 2a4.5 4.5 0 0 0-4.5 4.5c0 2.5-.8 3.5-1.5 4h12c-.7-.5-1.5-1.5-1.5-4A4.5 4.5 0 0 0 8 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
              <path d="M6.5 13a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: 3, right: 3, minWidth: 14, height: 14, borderRadius: 7, background: 'var(--red)', border: '1.5px solid var(--card)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--fu)', fontSize: 8, fontWeight: 700, color: '#fff', lineHeight: 1, padding: '0 3px' }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (() => {
            const visibleFiles = notifFiles.filter(f => (f.uploaded_at || '') > dismissed)
            const visibleMessages = notifMessages.filter(m => m.created_at > dismissed)
            const hasAny = notifApprovals.length > 0 || notifBriefs.length > 0 || visibleFiles.length > 0 || visibleMessages.length > 0 || notifInvoicesDue.length > 0
            const hasDismissable = visibleFiles.length > 0 || visibleMessages.length > 0
            function NewDot({ ts }) {
              return ts > lastSeen ? <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2563EB', flexShrink: 0, marginTop: 3 }} /> : null
            }
            return (
              <div style={{ position: 'absolute', top: 40, right: 0, width: 320, background: 'var(--card)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 200, overflow: 'hidden' }}>
                <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--bdr)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontFamily: 'var(--fd)', fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Notifications</span>
                  {hasAny ? (
                    <button onClick={markAllRead} style={{ fontFamily: 'var(--fu)', fontSize: 11, color: '#2563EB', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 500 }}>
                      Mark all as read
                    </button>
                  ) : (
                    <span style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)' }}>All clear</span>
                  )}
                </div>

                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {!hasAny ? (
                    <div style={{ padding: '28px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: 22, marginBottom: 8 }}>🔔</div>
                      <div style={{ fontFamily: 'var(--fu)', fontSize: 12, color: 'var(--hint)' }}>No new notifications</div>
                    </div>
                  ) : (
                    <>
                      {notifBriefs.length > 0 && (
                        <div>
                          <div style={{ padding: '8px 16px 4px', fontFamily: 'var(--fu)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--hint)' }}>Action Required</div>
                          {notifBriefs.map(b => (
                            <button key={b.id} onClick={() => { navigate('/brief'); setNotifOpen(false) }}
                              style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--off)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            >
                              <div style={{ width: 28, height: 28, borderRadius: 7, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="2" y="1.5" width="9" height="10" rx="1.5" stroke="#D97706" strokeWidth="1.2"/><path d="M4.5 4.5h4M4.5 6.5h4M4.5 8.5h2" stroke="#D97706" strokeWidth="1.1" strokeLinecap="round"/></svg>
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 1 }}>Project Brief Requested</div>
                                <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.projects?.title || 'Your project'} — tap to fill out →</div>
                              </div>
                              <NewDot ts={b.created_at} />
                            </button>
                          ))}
                        </div>
                      )}

                      {notifApprovals.length > 0 && (
                        <div>
                          <div style={{ padding: '8px 16px 4px', fontFamily: 'var(--fu)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--hint)' }}>Pending Approval</div>
                          {notifApprovals.map(a => (
                            <button key={a.id} onClick={() => { navigate('/approvals'); setNotifOpen(false) }}
                              style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--off)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            >
                              <div style={{ width: 28, height: 28, borderRadius: 7, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 1.5v5.5l3 2" stroke="#D97706" strokeWidth="1.3" strokeLinecap="round"/><circle cx="6.5" cy="6.5" r="5.5" stroke="#D97706" strokeWidth="1.3"/></svg>
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title || 'Approval needed'}</div>
                                <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)' }}>Tap to review and respond →</div>
                              </div>
                              <NewDot ts={a.created_at} />
                            </button>
                          ))}
                        </div>
                      )}

                      {visibleFiles.length > 0 && (
                        <div>
                          <div style={{ padding: '8px 16px 4px', fontFamily: 'var(--fu)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--hint)' }}>New Files (Last 7 Days)</div>
                          {visibleFiles.slice(0, 5).map(f => (
                            <button key={f.id} onClick={() => { navigate('/files'); setNotifOpen(false) }}
                              style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--off)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            >
                              <div style={{ width: 28, height: 28, borderRadius: 7, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                                <svg width="12" height="13" viewBox="0 0 12 13" fill="none"><path d="M2 2h6l2 2v8H2V2z" stroke="#2563EB" strokeWidth="1.2" strokeLinejoin="round"/><path d="M7 2v3h3" stroke="#2563EB" strokeWidth="1.2" strokeLinejoin="round"/></svg>
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name || 'New file'}</div>
                                <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)' }}>Tap to view in Files & Assets →</div>
                              </div>
                              <NewDot ts={f.uploaded_at} />
                            </button>
                          ))}
                          {visibleFiles.length > 5 && (
                            <div style={{ padding: '4px 16px 10px', fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)' }}>+{visibleFiles.length - 5} more files</div>
                          )}
                        </div>
                      )}

                      {visibleMessages.length > 0 && (
                        <div>
                          <div style={{ padding: '8px 16px 4px', fontFamily: 'var(--fu)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--hint)' }}>New Messages</div>
                          {visibleMessages.slice(0, 3).map(m => (
                            <button key={m.id} onClick={() => { navigate('/messages'); setNotifOpen(false) }}
                              style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--off)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            >
                              <div style={{ width: 28, height: 28, borderRadius: 7, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                                <svg width="13" height="11" viewBox="0 0 13 11" fill="none"><rect x="0.5" y="0.5" width="12" height="8" rx="1.5" stroke="#2563EB" strokeWidth="1.1"/><path d="M0.5 2.5l6 3.5 6-3.5" stroke="#2563EB" strokeWidth="1"/></svg>
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 1 }}>New message from your studio</div>
                                <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.body?.substring(0, 60) || 'Tap to view →'}</div>
                              </div>
                              <NewDot ts={m.created_at} />
                            </button>
                          ))}
                        </div>
                      )}

                      {notifInvoicesDue.length > 0 && (
                        <div>
                          <div style={{ padding: '8px 16px 4px', fontFamily: 'var(--fu)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--hint)' }}>Due Soon</div>
                          {notifInvoicesDue.map(inv => {
                            const daysLeft = Math.ceil((new Date(inv.due_date) - new Date()) / 86400000)
                            return (
                              <button key={inv.id} onClick={() => { navigate('/invoices'); setNotifOpen(false) }}
                                style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'var(--off)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                              >
                                <div style={{ width: 28, height: 28, borderRadius: 7, background: inv.status === 'overdue' ? '#FEE2E2' : '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="0.5" y="2" width="12" height="10.5" rx="1.5" stroke={inv.status === 'overdue' ? '#DC2626' : '#D97706'} strokeWidth="1.1"/><path d="M4.5 7h4M4.5 9h2" stroke={inv.status === 'overdue' ? '#DC2626' : '#D97706'} strokeWidth="1.1" strokeLinecap="round"/></svg>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 1 }}>{inv.invoice_number} — ${Number(inv.amount || 0).toLocaleString('en-US')}</div>
                                  <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: inv.status === 'overdue' ? '#DC2626' : 'var(--hint)' }}>
                                    {inv.status === 'overdue' ? 'Overdue' : daysLeft === 0 ? 'Due today' : `Due in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}
                                  </div>
                                </div>
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div style={{ padding: '10px 16px', borderTop: '1px solid var(--bdr)', display: 'flex', justifyContent: 'center' }}>
                  <button onClick={() => { navigate('/approvals'); setNotifOpen(false) }} style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--hint)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    View all activity →
                  </button>
                </div>
              </div>
            )
          })()}
        </div>
      )}

      <HeaderShortcuts
        isAdmin={isAdmin}
        counts={{ approvals: isAdmin ? adminPendingApprovals.length : notifApprovals.length,
                  draftInvoices: adminDraftInvoices.length }}
        sectionCounts={{ approvals: notifApprovals.length }}
      />

      <ThemeToggle compact />

      {isAdmin && <NewMenu />}

      {/* Avatar */}
      <div
        onClick={!isAdmin ? () => setPwOpen(true) : undefined}
        title={!isAdmin ? 'Change password' : undefined}
        style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, cursor: !isAdmin ? 'pointer' : 'default' }}>
        <div style={{
          width: 30, height: 30, background: 'var(--surf)', border: '1px solid var(--bdr)', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: "var(--fd)", fontSize: 11, fontWeight: 700, color: 'var(--txt)', flexShrink: 0,
        }}>
          {initials}
        </div>
        <div className="topbar-avatar-label">
          <div style={{ fontFamily: "var(--fu)", fontSize: 12, fontWeight: 500, color: 'var(--txt)' }}>
            {whoName || 'Client'}
          </div>
          {client?.company && (
            <div style={{ fontFamily: "var(--fu)", fontSize: 10, color: 'var(--hint)' }}>
              {client.company}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  )
}
